// Compliance Override modal — captures the user's reason for accepting a
// warning that falls outside BS / NHBC / mfr guidance, then POSTs to
// /api/quotes/{id}/overrides so it's recorded in the audit trail and
// surfaces on the PDF quote.
import React, { useState } from "react";
import { X, Warning, CheckCircle } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";

export default function ComplianceOverrideModal({ open, onClose, quoteId, warning, onSaved }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  if (!open || !warning) return null;

  const submit = async () => {
    if (!reason.trim()) {
      toast.error("A reason is required for the audit trail.");
      return;
    }
    if (!quoteId) {
      toast.error("Save the quote first, then add overrides.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        rule: warning.rule,
        standard: warning.standard,
        clause: warning.clause,
        field: warning.field,
        value: String(warning.value ?? ""),
        reason: reason.trim(),
        severity: warning.severity || "warning",
      };
      const { data } = await api.post(`/quotes/${quoteId}/overrides`, payload);
      toast.success("Override recorded in audit trail");
      onSaved?.(data);
      setReason("");
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/60 backdrop-blur-sm p-4" data-testid="override-modal">
      <div className="bg-white rounded-sm border border-slate-300 max-w-xl w-full shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 p-4">
          <div className="flex items-center gap-2">
            <Warning size={18} weight="fill" className="text-amber-600" />
            <h3 className="font-display font-bold text-lg">Record compliance override</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="override-close">
            <X size={18} weight="bold" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-amber-50 border border-amber-200 p-3 rounded-sm text-sm">
            <div className="font-semibold text-amber-900">{warning.message}</div>
            <div className="text-[11px] uppercase tracking-wider text-amber-700 mt-1">
              {warning.standard}{warning.clause ? ` · ${warning.clause}` : ""}
              {warning.section_name ? ` · ${warning.section_name}` : ""}
            </div>
            {warning.suggested && (
              <div className="text-xs text-amber-800 mt-1">Suggested: <span className="font-mono-data font-bold">{String(warning.suggested)}</span></div>
            )}
          </div>

          <label className="block">
            <span className="overline">Reason for override (required)</span>
            <textarea
              rows={4}
              className="rqp mt-1.5"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Client instructed to retain existing pitch on heritage building; manufacturer technical confirmed acceptable with additional clips."
              data-testid="override-reason"
            />
          </label>

          <div className="text-xs text-slate-500 leading-relaxed">
            By recording this override, you confirm that you have considered the {warning.standard} requirement and that you accept professional responsibility for the deviation. The override is added to the audit trail and printed on the PDF quote with your name and timestamp.
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 p-3 bg-slate-50">
          <button onClick={onClose} className="btn-secondary" data-testid="override-cancel">Cancel</button>
          <button onClick={submit} disabled={saving} className="btn-primary flex items-center gap-2" data-testid="override-submit">
            <CheckCircle size={14} weight="bold" /> {saving ? "Saving…" : "Record override"}
          </button>
        </div>
      </div>
    </div>
  );
}
